// Admin casino "My Bets" card helpers (ported from tenexch CasinoMyBetsSidebar).

// The card lists only the LATEST 10 bets (View More has the rest), kept in the
// caller's own order. Display only — the page's book still uses every bet.
export const MY_BETS_LIMIT = 10;
export function latestBets(bets) {
  const list = Array.isArray(bets) ? bets : [];
  if (list.length <= MY_BETS_LIMIT) return list;
  const time = (b) => new Date(b.created_at || b.placeDate || b.createdAt || 0).getTime();
  const keep = new Set([...list].sort((a, b) => time(b) - time(a)).slice(0, MY_BETS_LIMIT));
  return list.filter((b) => keep.has(b));
}

// Note Number card-value bets are stored as "Card 4 A" (the shape settlement
// parses); shown as "Card 4 - Card A", like the player site. Display only.
export function formatNoteNumNation(nat) {
  const n = String(nat ?? '').trim();
  const m = n.match(/^Card\s+(\d+)\s+(A|10|[2-9])$/i);
  return m ? `Card ${m[1]} - Card ${m[2].toUpperCase()}` : n;
}

// Instant Teenpatti (teen3 / teen32 / teen33): bets are stored as "Player A" / "Player B";
// the reference prints the main market as "Player A-Main". Display only.
export function formatMainNation(nat) {
  const n = String(nat ?? '').trim();
  return n === 'Player A' || n === 'Player B' ? `${n}-Main` : n;
}

// Teenpatti 1-day: bets store "Player A" for Main (mtype "match") and Consecutive
// (mtype "fancy"), and "Card 2 Odd"; the reference admin prints "Player A-Main",
// "Player B-CON" and "Card 2 - Odd". Display only.
export function formatTeenOneDayNation(nat, bet) {
  const n = String(nat ?? '').trim();
  if (n === 'Player A' || n === 'Player B') {
    return `${n}-${String(bet?.mtype || 'match').toLowerCase() === 'match' ? 'Main' : 'CON'}`;
  }
  const m = n.match(/^(Card \d+) (Odd|Even)$/i);
  return m ? `${m[1]} - ${m[2]}` : n;
}

// Teenpatti 2.0 (teen6), the reference way (tenexch formatTeen6Nation): "Player A"
// → "Player A-Main", "Player A Under 21" → "Player A Under 21-UO", "Card 5 Diamond"
// → "Card 5 - Diamond", "Card 1 Odd" → "Card 1 - Odd", "Card 1 A" → "Card 1 - Card A".
// Display only.
export function formatTeen6Nation(nat) {
  const n = String(nat ?? '').trim();
  if (/^Player [AB]$/i.test(n)) return `${n}-Main`;
  if (/^Player [AB] (Under|Over) \d+$/i.test(n)) return `${n}-UO`;
  const side = n.match(/^Card (\d) (Spade|Heart|Club|Diamond|Odd|Even)$/i);
  if (side) return `Card ${side[1]} - ${side[2]}`;
  const face = n.match(/^Card (\d) (A|J|Q|K|\d{1,2})$/i);
  return face ? `Card ${face[1]} - Card ${face[2].toUpperCase()}` : n;
}

// Unique Teenpatti: a bet stores the picked positions ("234"); the reference
// names it "Your card 234". Display only.
export function formatUniqueTeenNation(nat) {
  const s = String(nat ?? '').trim();
  return /^[1-6]{3}$/.test(s) ? `Your card ${s}` : nat;
}

// 2 Cards Teenpatti, the reference way: "Player A-Main", "Mini Baccarat A-Baccarat",
// "Color Plus-Plus", and a Total bet (stored "Total A 14" = side + LINE, at the
// decimal bhav/100 + 1) → "Total A-95", with the line (14) as its rate. Display only.
const PATTI2_TOTAL = /^Total ([AB]) (\d+)$/i;
export function formatPatti2Nation(name, bet) {
  const sel = String(bet?.selection || name || '').trim();
  const t = sel.match(PATTI2_TOTAL);
  const odds = Number(bet?.odds ?? bet?.rate);
  if (t && odds > 1) return `Total ${t[1].toUpperCase()}-${Math.round((odds - 1) * 100)}`;
  if (/^Player [AB]$/i.test(sel)) return `${sel}-Main`;
  if (/^Mini Baccarat [AB]$/i.test(sel)) return `${sel}-Baccarat`;
  if (/^Color Plus$/i.test(sel)) return 'Color Plus-Plus';
  return name;
}

// Teenpatti Joker 20-20: "<name> - <market>" in the reference's spelling ("Player B - Player",
// "Joker Black - redblack", "Joker Spade - card"). Display only.
const JOKER20_MARKETS = {
  'player a': 'Player', 'player b': 'Player',
  'joker even': 'oddeven', 'joker odd': 'oddeven',
  'joker red': 'redblack', 'joker black': 'redblack',
  'joker spade': 'card', 'joker heart': 'card', 'joker diamond': 'card', 'joker club': 'card',
};
export function formatJoker20Nation(nat) {
  const s = String(nat ?? '').trim();
  const market = JOKER20_MARKETS[s.toLowerCase()];
  return market ? `${s} - ${market}` : nat;
}

// Unlimited Joker pick 1..13 → card label (1 = A, 11 = J, 12 = Q, 13 = K).
const jokerLabel = (n) => ({ 1: 'A', 11: 'J', 12: 'Q', 13: 'K' }[n] || String(n));

// Race 20 Total bet as stored: market + LINE ("Total cards 13"); Trio Session line.
const RACE20_TOTAL = /^Total (points|cards) (\d+)$/i;
// Mogambo 3 Card Total bet as stored: "3 Card Total 20" at rate/100 + 1.
const MOGAMBO_TOTAL = /^3 Card Total (\d+)$/i;
const TRIO_SESSION_LINE = 21;
// Super Over Fancy session bet as stored: row + run LINE ("1 over run ENG 13" @1.84).
const SUPEROVER_SESSION = /^(\d+\s+over\s+run(?:\s+bhav)?\s+\S+)\s+(\d+(?:\.\d+)?)$/i;

// My Bets / View More print a session bet as "1 over run ENG / 84" with its run
// line (13) as the rate, like the reference (tenexch); null for any other bet.
export function superOverSessionBet(bet) {
  const m = String(bet?.selection || bet?.player_name || '').trim().match(SUPEROVER_SESSION);
  return m ? { nation: `${m[1]} / ${Math.round(((Number(bet.odds ?? bet.userrate) || 0) - 1) * 100)}`, rate: m[2] } : null;
}

// ---- Worli / Instant Worli / Matka (tenexch admin casinoUtils) ----
// Worli / Instant Worli bet names the reference way (bet slip, My Bets, result
// modals). The boards store a hyphenated key — "single-3", "single-line1",
// "pana-590", "sp-pana-590", "sp-all", "trio-all" — and settlement matches on
// that key, so this is display only: "single-3" → "3 Single",
// "single-line1" → "Line 1 Single", "single-odd" → "Odd Single",
// "pana-590" → "590 Pana", "sp-pana-590" → "590 SP", "sp-all" → "All SP".
const WORLI_SUFFIX = { line1: 'Line 1', line2: 'Line 2', odd: 'Odd', even: 'Even', all: 'ALL', cut: 'Cut' };
const WORLI_LABELS = {
  single: 'Single', pana: 'Pana', sp: 'SP', dp: 'DP', tp: 'TP', trio: 'Trio',
  cycle: 'Cycle', motorsp: 'Motor SP', c56: '56', c64: '64', abr: 'ABR',
  commonsp: 'Common SP', commondp: 'Common DP', colordp: 'color DP', // reference result modal: "1 color DP" (slip keeps "Color DP - 1")
};
// Matka order for a digit set: ascending, 0 last (4-2-5 → 245, 6-0-4 → 460) — twin of the frontend helper.
function formatMatkaPana(digits) {
  const d = (digits || []).map(Number).sort((a, b) => a - b);
  return d.filter((x) => x !== 0).join('') + '0'.repeat(d.filter((x) => x === 0).length);
}
// Matka (worli3) names the reference way (admin View More, user 2026-10-17):
// Jodi "0-2" → "2 Jodi" (the number, no leading zero), an exact pana
// "sp-pana-157" / "dp-pana-112" / "tp-pana-111" → "157 Pana"; everything else
// as Worli ("1 SP", "Line 1 Single", "1 color DP"). Display only.
function formatWorli3Nation(nat) {
  const s = String(nat || '').trim();
  const jodi = s.match(/^(\d)-(\d)$/);
  if (jodi) return `${Number(jodi[1] + jodi[2])} Jodi`;
  const pana = s.match(/^(?:sp|dp|tp)-pana-(\d{3})$/i);
  if (pana) return `${pana[1]} Pana`;
  return formatWorliNation(s);
}

function formatWorliNation(nat) {
  const m = String(nat || '').trim().match(/^([a-z0-9]+)-(.+)$/i);
  if (!m) return nat;
  const label = WORLI_LABELS[m[1].toLowerCase()];
  if (!label) return nat;
  const raw = m[2].replace(/^pana-/i, '');
  // Motor SP digits print in Matka order — ascending, 0 last ("0137" → "1370"), like the strip (user).
  if (label === 'Motor SP' && /^\d+$/.test(raw)) return `${formatMatkaPana(raw.split(''))} ${label}`;
  const val = WORLI_SUFFIX[raw.toLowerCase()] || raw.charAt(0).toUpperCase() + raw.slice(1);
  // 56 / 64 charts: My Bets / result modals read "1 - Chart 56" on the reference
  // (the player's bet slip reads "56 - 1").
  // ALL cells print the word in caps everywhere (user): "ALL SP", "ALL - Chart 56".
  // 56 / 64 charts' ALL cell drops the dash (user): "ALL Chart 56" / "ALL Chart 64".
  if ((label === '56' || label === '64') && raw.toLowerCase() === 'all') return `${val} Chart ${label}`;
  if (label === '56' || label === '64') return `${val} - Chart ${label}`;
  // ABR (reference result modal): "A Only ABR" / "B Only ABR" / "R Only ABR", the ABR cell "ABR Only".
  if (label === 'ABR') {
    if (raw.toLowerCase() === 'all') return 'ABR Only';
    if (raw.toLowerCase() === 'cut') return 'ABR Cut Only';
    if (/^(a|b|r|ab|ar|br)$/i.test(raw)) return `${raw.toUpperCase()} Only ABR`; // combos too: "AB Only ABR"
    return `${raw.toUpperCase()} ABR`;
  }
  return `${val} ${label}`;
}

// Worli Matka / Instant Worli Line 1 / Line 2 / Odd / Even: the reference places ONE bet per
// number in the group (5 numbers) at the board rate 9 — stake 100 → 500 placed,
// a hit pays 800 on one number and loses 400 on the other four (net +400), a
// miss loses all 500. We store that as one bet: stake × 5 at 9 ÷ 5 = 1.8, so the
// generic book (stake) and settlement (stake × odds − stake) land on the same
// 500 / +400 / −500 with no server change. The rate cell shows the board's 9.
const WORLI_LINE_UNITS = 5;
const isWorliLineSelection = (nat) => /^single-(line1|line2|odd|even)$/i.test(String(nat || '').trim());
// My Bets rate cell only: the reference prints 0 for a Cycle bet there (its result
// modal and statement print the stored 140). Everything else as formatWorliOdds.
export function formatWorliMyBetsOdds(bet, fallback) {
  const name = bet?.selection || bet?.player_name || bet?.nation || bet?.nat;
  if (/^cycle-/i.test(String(name || '').trim())) return '0';
  return formatWorliOdds(bet, fallback);
}
function formatWorliOdds(bet, fallback) {
  const name = bet?.selection || bet?.player_name || bet?.nation || bet?.nat;
  const odds = Number(bet?.odds ?? bet?.rate);
  if (!isWorliLineSelection(name) || !(odds > 0)) return fallback;
  return String(Math.round(odds * WORLI_LINE_UNITS * 100) / 100);
}

// Goal: rdesc "<player>#<method>" reads "<method> by <player>"; a no-goal round
// ("No Goal#No Goal") reads "No Goal" (tenexch formatGoalResultText).
export function formatGoalResultText(rdesc) {
  const [player, method] = String(rdesc || '').split('#').map((s) => s.trim());
  if (!method) return player || '';
  if (/^no goal$/i.test(method) || /^no goal$/i.test(player)) return 'No Goal';
  return player ? `${method} by ${player}` : method;
}

// Rate shown in My Bets / View More / round bets / statement, per game (tenexch
// formatBetOdds). `fallback` is the stored odds (or its net price). Display only.
//  - Muflis Top 9: a WON bet shows its multiple + 1 (×3 → 4 … ×30 → 31).
//  - 2 Cards Teenpatti / Race 20 Totals: the bet's line; Trio Session: its line 21.
//  - Andar Bahar 2 1st / 2nd Bet paid 1 : 0.5 → 1.5 (open / lost bets keep the stored odds).
// 1 Card Meter: stored at the feed's cell value, not a price. The round's rate is
// the card point difference (A=1 … K=13, max 12): rdesc "Fighter A#12", else the
// cards (same value other suit = 1; identical = tie, 0) — tenexch cmeter1RoundRate.
const cmeter1CardValue = (code) => {
  const rank = String(code || '').trim().toUpperCase().replace(/[SHCD]+$/, '');
  const named = { A: 1, J: 11, Q: 12, K: 13 }[rank];
  if (named) return named;
  const n = parseInt(rank, 10);
  return n >= 2 && n <= 10 ? n : null;
};
function cmeter1RoundRate(t1) {
  const cards = String(t1?.card || '').split(',').map((c) => c.trim().toUpperCase());
  const [a, b] = cards.map(cmeter1CardValue);
  if (a != null && b != null && cards[0] === cards[1]) return 0;
  const fromRdesc = parseInt(String(t1?.rdesc || '').split('#')[1], 10);
  if (fromRdesc > 0) return Math.min(fromRdesc, 12);
  if (a == null || b == null) return null;
  return Math.min(Math.abs(a - b) || 1, 12);
}

// `t1`: the round's result (round bets / statement) — 1 Card Meter reads its rate from it.
export function formatBetOdds(gameType, bet, fallback, t1) {
  // the stored selection first (round-bets rows carry it as `nation`); player_name can be the bare market ("Total cards")
  const name = String(bet?.selection || bet?.matchedBet || bet?.nation || bet?.player_name || bet?.nat || '').trim();
  const stake = Number(bet?.stake ?? bet?.amount);
  const win = Number(bet?.win);
  const paid = (part) => stake > 0 && win > 0 && Math.abs(win - stake * part) < 0.01;
  switch (gameType) {
    case 'teenmuf':
      return /^top\s*9\s*[ab]$/i.test(name) && stake > 0 && win > 0 ? Math.round(win / stake) + 1 : fallback;
    case 'patti2': {
      const t = name.match(PATTI2_TOTAL);
      return t ? Number(t[2]) : fallback;
    }
    case 'race20': {
      const t = name.match(RACE20_TOTAL);
      return t ? Number(t[2]) : fallback;
    }
    case 'trio':
      return /^session$/i.test(name) ? TRIO_SESSION_LINE : fallback;
    case 'abj':
      return /^(1st|2nd) Bet\b/i.test(name) && paid(0.5) ? 1.5 : fallback;
    case 'cmeter1':
      return cmeter1RoundRate(t1) ?? fallback;
    case 'mogambo': {
      const t = name.match(MOGAMBO_TOTAL);
      return t ? Number(t[1]) : fallback;
    }
    case 'superover2': { // the session bet's run line, like its My Bets (tenexch)
      const t = name.match(SUPEROVER_SESSION);
      return t ? Number(t[2]) : fallback;
    }
    case 'worli':
    case 'worli2':
    case 'worli3':
      return formatWorliOdds(bet, fallback); // Line / Odd / Even stored at rate ÷ 5 → shows 9.5 / 9
    default:
      return fallback;
  }
}

// Andar Bahar 50 (ab3): is this bet already decided by the cards dealt so far?
// Same rule as the settlement: the first card of the bet's rank at/after its
// deal position ("Andar Q/7"). The admin list drops it once that card is out.
export function isAb50BetDecided(bet, cardString) {
  const sel = String(bet?.selection || '').trim().match(/^(Andar|Bahar)\s+(10|[2-9]|[AJQK])$/i);
  if (!sel) return false;
  const rank = sel[2].toUpperCase();
  const pm = String(bet?.player_name || '').match(/\/\s*(\d+)\s*$/);
  const start = pm ? Number(pm[1]) - 1 : 0;
  const cards = String(cardString || '').split(',').map((c) => c.trim());
  for (let i = Math.max(0, start); i < cards.length; i += 1) {
    const c = cards[i];
    if (!c || c === '1' || c === '0') return false;
    if ((c.toUpperCase().match(/^(10|[2-9]|[AJQK])/) || [])[1] === rank) return true;
  }
  return false;
}

// Lottery: stored "Double 9 8" (play + digits); reads "Double-98" like the player site.
export function formatLottcardNation(nat, bet) {
  const m = String(bet?.selection || nat || '').trim().match(/^(\S+)\s+([\d\s]+)$/);
  return m ? `${m[1]}-${m[2].replace(/\s+/g, '')}` : nat;
}

// Roulettes: the bet type after the name, as the reference — '10 - Single',
// '1,2 - Split', '1st 12 - Dozen', 'Red - Half'; VIP / Beach / Roulette also
// '3rd Column - Column'. Unique Roulette bets carry their type in player_name
// ('5,6 - Street', by board position) — that wins. Display only.
const ROULETTE_GAMES = new Set(['ourroullete', 'roulette11', 'roulette12', 'roulette13']);
const ROULETTE_INSIDE_TYPES = { 1: 'Single', 2: 'Split', 3: 'Street', 4: 'Corner' };
const ROULETTE_OUTSIDE_TYPES = {
  '1st 12': 'Dozen', '2nd 12': 'Dozen', '3rd 12': 'Dozen',
  '1 to 18': 'Half', '19 to 36': 'Half', red: 'Half', black: 'Half', odd: 'Half', even: 'Half',
};
function formatRouletteNation(gameType, nat, bet) {
  const stored = String(bet?.player_name ?? '').trim();
  if (gameType === 'ourroullete' && /^\d+(\s*,\s*\d+)* - (Single|Split|Street|Corner)$/.test(stored)) return stored;
  const s = String(nat ?? '').trim();
  if (/^\d+(\s*,\s*\d+)*$/.test(s)) {
    const type = ROULETTE_INSIDE_TYPES[s.split(',').length];
    return type ? `${s} - ${type}` : nat;
  }
  if (gameType !== 'ourroullete' && /^(1st|2nd|3rd) column$/i.test(s)) return `${s} - Column`;
  const outside = ROULETTE_OUTSIDE_TYPES[s] || ROULETTE_OUTSIDE_TYPES[s.toLowerCase()];
  return outside ? `${s} - ${outside}` : nat;
}

// Per-game display name of a stored bet (result modal round bets, statement details).
export function formatBetNation(gameType, nat, bet) {
  if (ROULETTE_GAMES.has(gameType)) return formatRouletteNation(gameType, nat, bet);
  if (gameType === 'teen') return formatTeenOneDayNation(nat, bet);
  if (gameType === 'teen6') return formatTeen6Nation(nat);
  if (gameType === 'teenunique') return formatUniqueTeenNation(nat);
  if (gameType === 'patti2') return formatPatti2Nation(nat, bet);
  if (gameType === 'joker20') return formatJoker20Nation(nat);
  if (gameType === 'lottcard') return formatLottcardNation(nat, bet);
  // Mogambo 3 Card Total: stored "3 Card Total 20" at rate/100 + 1 → "3 Card Total-90".
  if (gameType === 'mogambo') {
    const odds = Number(bet?.odds ?? bet?.rate);
    return MOGAMBO_TOTAL.test(String(bet?.selection || nat || '').trim()) && odds > 1 ? `3 Card Total-${Math.round((odds - 1) * 100)}` : nat;
  }
  // Worli / Instant Worli / Matka board keys the reference way ("sp-all" → "ALL SP", "3-5" → "35 Jodi").
  if (gameType === 'worli' || gameType === 'worli2') return formatWorliNation(nat);
  if (gameType === 'worli3') return formatWorli3Nation(nat);
  // Andar Bahar 150: the name with its deal position ("Bahar A/136", player_name).
  if (gameType === 'ab4' && /\/\s*\d+\s*$/.test(String(bet?.player_name || ''))) return bet.player_name;
  // Andar Bahar 2: "1st Bet SB" / "2nd Bet SA" read "1st Bet B" / "2nd Bet A".
  if (gameType === 'abj') return String(nat || '').replace(/^((?:1st|2nd) Bet) S([AB])$/i, '$1 $2');
  // 20-20 Poker: Winner is stored "Player A/B"; it reads "Winner A/B".
  if (gameType === 'poker20') {
    const m = String(nat || '').trim().match(/^Player ([AB])$/i);
    return m ? `Winner ${m[1].toUpperCase()}` : nat;
  }
  // Trio Session: stored "Session" at rate/100 + 1 → "Session-80" / "Session-100".
  if (gameType === 'trio') {
    const odds = Number(bet?.odds ?? bet?.rate);
    return /^session$/i.test(String(nat || '').trim()) && odds > 1 ? `Session-${Math.round((odds - 1) * 100)}` : nat;
  }
  // Race 20 Totals: stored "Total cards 13" at rate/100 + 1 → "Total cards / 90".
  if (gameType === 'race20') {
    const t = String(bet?.selection || nat || '').trim().match(RACE20_TOTAL);
    const odds = Number(bet?.odds ?? bet?.rate);
    return t && odds > 1 ? `Total ${t[1].toLowerCase()} / ${Math.round((odds - 1) * 100)}` : nat;
  }
  // The Trap: "Player A-Main"; side bets "Card 1 JQK" / "Card 3 Low" → "Card 1 - JQK" / "Card 3 - Low".
  if (gameType === 'trap') {
    const m = String(nat || '').trim().match(/^Card\s+(\d+)\s+(Low|High|JQK)$/i);
    if (m) return `Card ${m[1]} - ${m[2].toUpperCase() === 'JQK' ? 'JQK' : m[2][0].toUpperCase() + m[2].slice(1).toLowerCase()}`;
    return formatMainNation(nat);
  }
  // Unlimited Joker: the joker the bet was placed with — "Player A - Joker J".
  if ((gameType === 'joker1' || gameType === 'joker120') && Number(bet?.joker)) return `${nat} - Joker ${jokerLabel(Number(bet.joker))}`;
  // Jack Top / Queen Top Open: same markets and names as teen6 (reference).
  if (gameType === 'teen41' || gameType === 'teen42') return formatTeen6Nation(nat);
  if (gameType === 'notenum') return formatNoteNumNation(nat);
  if (gameType === 'teen3' || gameType === 'teen32' || gameType === 'teen33') return formatMainNation(nat);
  return nat;
}

// Bottom edge of the fixed header on screen. Children can overflow #page-topbar,
// so its own height is not enough — take the lowest visible child too.
function headerBottom() {
  const header = document.getElementById('page-topbar');
  if (!header) return 0;
  let bottom = header.getBoundingClientRect().bottom;
  for (const child of header.querySelectorAll('*')) {
    const r = child.getBoundingClientRect();
    if (r.height > 0 && r.bottom > bottom && r.bottom < window.innerHeight / 2) bottom = r.bottom;
  }
  return Math.max(0, bottom);
}

// Mobile "Bets" badge: land the My Bets card right under the fixed header (plain
// scrollIntoView hid its heading behind it). The page can shift during the smooth
// scroll (live odds / cards), so snap to the exact spot once it stops.
export function scrollToMyBets(id = 'casino-bets') {
  const el = document.getElementById(id);
  if (!el) return;
  const targetTop = () => Math.max(0, el.getBoundingClientRect().top + window.scrollY - headerBottom());
  window.scrollTo({ top: targetTop(), behavior: 'smooth' });
  let done = false;
  const fix = () => {
    if (done) return;
    done = true;
    window.removeEventListener('scrollend', fix);
    if (Math.abs(window.scrollY - targetTop()) > 2) window.scrollTo({ top: targetTop(), behavior: 'instant' });
  };
  window.addEventListener('scrollend', fix);
  setTimeout(fix, 1000); // browsers without scrollend
}
